// The audio / video element as a surface (audio files, radio streams, video files): its events as MediaEvents, a
// handle for the follower, "Tap to listen" when play() is refused (or, with `silenceIsBlocked`, a silent start).
import { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import AudioPlayer from "../AudioPlayer";
import VideoPlayer from "../VideoPlayer";
import { videoState, type MediaHandle } from "../media";
import type { SurfaceProps } from "../PlayableMedia";
import { ErrorOverlay, Frame, useEmit, useLatest, useTapToListen } from "./shared";
import ExternalMediaPlaceholder, { externalSiteName, useExternalMediaAllowed } from "../ExternalMediaPlaceholder";
import { isFirstPartyUrl } from "../../lib/externalMedia";

const finiteMs = (s: number): number | undefined => (Number.isFinite(s) ? s * 1000 : undefined);

/**
 * Privacy consent: a file or stream from another host (an Icecast radio, a third-party MP3 / HLS / video URL) is not
 * requested until this browser allows external media — the same click-to-load placeholder as hosted embeds. "Load"
 * plays this one URL (nothing recorded); "Always allow" = external_media ACCEPTED. Our own files load at once.
 */
export default function ElementSurface(p: SurfaceProps) {
  const allowed = useExternalMediaAllowed();
  const [loadedOnce, setLoadedOnce] = useState<string | null>(null);
  if (allowed || loadedOnce === p.url || isFirstPartyUrl(p.url)) return <MediaElementSurface {...p} />;
  const placeholder = (
    <ExternalMediaPlaceholder
      key={p.url}
      site={externalSiteName(null, p.url)}
      music={p.kind.surface === "audio"}
      onLoad={() => setLoadedOnce(p.url)}
    />
  );
  return <Frame frame={p.frame} media={placeholder} overlays={null} />;
}

function MediaElementSurface(p: SurfaceProps) {
  const { t } = useTranslation();
  const { url, kind } = p;
  const video = kind.surface === "video";
  const live = kind.caps.live;
  const emit = useEmit(p);
  const el = useRef<HTMLMediaElement | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [ended, setEnded] = useState(false);
  const [failed, setFailed] = useState(false);
  const blockedRef = useRef<() => void>(() => {});

  const handle = useMemo<MediaHandle>(
    () => ({
      // play() rejects when the browser wants a tap first (NotAllowedError); other errors fire `error`.
      play: () => {
        el.current?.play()?.catch?.((err: unknown) => {
          if ((err as { name?: string })?.name === "NotAllowedError") blockedRef.current();
        });
      },
      pause: () => el.current?.pause(),
      seek: (ms) => {
        if (el.current) el.current.currentTime = ms / 1000;
      },
      setRate: (rate) => {
        if (el.current) el.current.playbackRate = rate;
      },
      reload: () => el.current?.load(),
      sample: () => {
        const a = el.current;
        const src = a?.getAttribute("src");
        if (!a || !src) return null;
        return { key: src, positionMs: a.currentTime * 1000, state: videoState(a), durationMs: finiteMs(a.duration) };
      },
    }),
    [],
  );
  useImperativeHandle(p.handleRef, () => handle, [handle]);

  const armed = p.silenceIsBlocked === true && !!p.shouldPlay && loaded && !playing && !ended && !failed;
  const tap = useTapToListen(p, armed, handle.play, p.face === true || video);
  blockedRef.current = () => tap.setBlocked(true);

  // New media: nothing of the last one holds.
  useEffect(() => {
    setLoaded(false);
    setPlaying(false);
    setEnded(false);
    setFailed(false);
  }, [url]);

  // The one play() at mount when asked (no autoPlay attribute): its refusal is how a blocked start shows.
  const autoPlay = useLatest(p.autoPlay);
  useEffect(() => {
    const a = autoPlay.current;
    if (typeof a === "function" ? a() : a === true) handle.play();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlers = {
    ref: (node: HTMLMediaElement | null) => {
      el.current = node;
    },
    src: url,
    testId: p.hostTestId,
    onLoadedMetadata: () => {
      const a = el.current;
      if (!a || a.getAttribute("src") !== url) return;
      setLoaded(true);
      emit({ type: "loaded", key: url, durationMs: finiteMs(a.duration) });
    },
    onPlaying: () => {
      setPlaying(true);
      setEnded(false);
      tap.setBlocked(false);
      emit({ type: "state", key: url, state: "playing", positionMs: (el.current?.currentTime ?? 0) * 1000 });
    },
    onPause: () => {
      setPlaying(false);
      emit({ type: "state", key: url, state: "paused", positionMs: (el.current?.currentTime ?? 0) * 1000 });
    },
    onWaiting: () => {
      setPlaying(false);
      emit({ type: "state", key: url, state: "buffering", positionMs: (el.current?.currentTime ?? 0) * 1000 });
    },
    onEnded: () => {
      setPlaying(false);
      setEnded(true);
      emit({ type: "ended", key: url });
    },
    onError: () => {
      setFailed(true);
      emit({ type: "error", key: url });
    },
    onTimeUpdate: () => {
      const a = el.current;
      if (!a) return;
      emit({ type: "position", key: url, positionMs: a.currentTime * 1000, durationMs: live ? null : (finiteMs(a.duration) ?? null) });
    },
  };

  if (video) {
    const media = <VideoPlayer {...handlers} playsInline className={p.hostClassName ?? "w-full h-full object-contain"} />;
    return (
      <>
        <Frame frame={p.frame} media={media} overlays={<>{failed && p.showError && <ErrorOverlay />}{tap.overlay(failed)}</>} />
        {tap.below(failed)}
      </>
    );
  }
  const audio = <AudioPlayer {...handlers} style={{ display: "none" }} />;
  if (!p.face) return <>{audio}{tap.below(failed)}</>;
  // The audio face (a cast card): the kind's glyph and the item's name over the frame.
  const face = (
    <>
      {audio}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 bg-[#0f172a] p-3 text-center text-[13px] text-white">
        <span aria-hidden className="text-[28px]">{live ? "📻" : "♪"}</span>
        <span className="truncate max-w-full">{p.item.title || kind.label(t)}</span>
      </div>
    </>
  );
  return (
    <>
      <Frame frame={p.frame} media={face} overlays={<>{failed && p.showError && <ErrorOverlay />}{tap.overlay(failed)}</>} />
      {tap.below(failed)}
    </>
  );
}
